/**
 * voice-v8 — Anpassender Abfragetakt (2026-07-31)
 *
 * Martin: "Bitte prüfe auch, inwieweit jetzt Energie verbraucht wird, weil
 * mein Akku immer extrem schnell leer ist."
 *
 * GEMESSEN am 31.07., eine Stunde Normalbetrieb: 2.708 Anfragen ans Netz,
 * also 45 pro Minute. Die sechs häufigsten Statusabfragen machten davon
 * 1.872 aus — allein `/api/system/busy` 426 Mal, alle acht Sekunden.
 *
 * Zum Vergleich: Beim Bündeln der Aufnahme-Häppchen am 27.07. wurden 180
 * Funkweckungen pro Stunde auf sechs gesenkt, und das war der größte
 * Energiegewinn bisher. Diese sechs Abfragen zusammen sind das Zehnfache
 * dessen, was damals eingespart wurde.
 *
 * Der Fehler ist nicht das Abfragen an sich, sondern der STARRE Takt: Ob
 * gerade eine Aufnahme verarbeitet wird, ändert sich vielleicht dreimal am
 * Tag — abgefragt wurde es 426 Mal pro Stunde. Was hier steht, hält das
 * Tempo dort, wo etwas passiert, und wird langsam, wo nichts passiert.
 *
 * Nicht angetastet: die Sichtbarkeitsprüfung. Eine Abfrage bei App im
 * Hintergrund findet weiterhin gar nicht statt — das war schon richtig.
 */

import { beiRuckelAenderung, gesteuert, ruckelAus } from "./ruckel-schalter.js";

interface TaktOptionen {
  /** Was abgefragt wird. Gibt zurueck, ob sich etwas GEAENDERT hat. */
  abfrage: () => Promise<boolean>;
  /** Takt, solange sich etwas tut. */
  schnellMs: number;
  /** Langsamster Takt bei Stillstand. */
  langsamMs: number;
  /** Fuer die Fehlersuche im Protokoll. */
  name: string;
  /** Der Ruckel-Test (Ticket 2912) pausiert diesen Takt nicht — nur fuer Meldungen an den Server. */
  nichtPausieren?: boolean;
}

/**
 * Startet eine Abfrage, deren Takt sich an die Wirklichkeit anpasst.
 *
 * Nach jeder Runde ohne Aenderung wird der Abstand um die Haelfte gestreckt,
 * bis `langsamMs` erreicht ist. Sobald sich etwas aendert, springt er sofort
 * auf `schnellMs` zurueck — die Anzeige bleibt also flink, wenn es darauf
 * ankommt, und still, wenn nicht.
 *
 * Gibt eine Funktion zum Anhalten zurueck.
 */
export function starteAnpassendenTakt(opts: TaktOptionen): () => void {
  let abstand = opts.schnellMs;
  let timer: number | null = null;
  let gestoppt = false;
  let verpasst = false;

  const runde = async (): Promise<void> => {
    if (gestoppt) return;
    // Ruckel-Test (Ticket 2912), „Live-Aktualisierungen pausieren“: nicht fragen, beim Ausschalten sofort nachholen.
    if (!opts.nichtPausieren && ruckelAus("live")) {
      verpasst = true;
      timer = window.setTimeout(() => void runde(), abstand);
      return;
    }
    // Bei unsichtbarer App gar nicht erst fragen — und den Abstand dabei
    // strecken, damit ein wiedererwachtes Telefon nicht sofort hämmert.
    if (document.visibilityState !== "visible") {
      abstand = opts.langsamMs;
    } else {
      try {
        const geaendert = await opts.abfrage();
        abstand = geaendert
          ? opts.schnellMs
          : Math.min(opts.langsamMs, Math.round(abstand * 1.5));
      } catch {
        // Netzfehler wie "nichts geaendert" behandeln: strecken statt
        // hämmern. Ein totes Netz durch schnelleres Fragen zu beleben,
        // kostet nur Strom.
        abstand = Math.min(opts.langsamMs, Math.round(abstand * 1.5));
      }
    }
    if (!gestoppt) timer = window.setTimeout(() => void runde(), abstand);
  };

  // Kommt die App aus dem Hintergrund zurueck, sofort einmal fragen und
  // wieder schnell werden — sonst sieht der Nutzer veraltete Zahlen.
  const beiSichtbar = (): void => {
    if (document.visibilityState !== "visible" || gestoppt) return;
    abstand = opts.schnellMs;
    if (timer !== null) window.clearTimeout(timer);
    void runde();
  };
  document.addEventListener("visibilitychange", beiSichtbar);
  const abmelden = beiRuckelAenderung((id, jetztAn) => {
    if (id !== "live" || jetztAn || !verpasst || gestoppt) return;
    verpasst = false;
    if (timer !== null) window.clearTimeout(timer);
    void runde();
  });

  void runde();

  return () => {
    gestoppt = true;
    if (timer !== null) window.clearTimeout(timer);
    document.removeEventListener("visibilitychange", beiSichtbar);
    abmelden();
  };
}

/**
 * Ein Takt, der bei dunklem Display schweigt. (17.09.2026)
 *
 * ## Woher das kommt
 *
 * Martin: *„Jetzt läuft es zwar, aber der verbraucht so viel Energie, dass er
 * innerhalb von drei Stunden alles leer ist. Das Handy ist total heiß."*
 *
 * Am 17.09. im Browser gemessen, je eine Minute:
 *
 *     sichtbar    13 Anfragen/min
 *     versteckt    7 Anfragen/min   ← und die liefen einfach weiter
 *
 * Sieben je Minute sind in drei Stunden **1.260 Funkweckungen**. Das
 * Funkmodul kommt dabei nie in den Schlafzustand, und genau das waermt das
 * Telefon — nicht die Aufnahme selbst.
 *
 * ⚠️ Warum das erst jetzt auffiel: Vor dem 17.09. riss die Aufnahme im
 *    Hintergrund ab, iOS legte die App schlafen, und die Abfragerei hoerte
 *    von selbst auf. Seit sie durchlaeuft, haelt sie die App wach — und die
 *    Abfragen laufen drei Stunden mit. Der Akku bezahlt eine Reparatur an
 *    einer ganz anderen Stelle.
 *
 * ⚠️ Kein Nachholen beim Zurueckkommen. Wer eine verpasste Runde nachholt,
 *    baut sich einen Stau, der beim Aufwachen auf einmal losfaehrt. Der
 *    naechste regulaere Schlag genuegt; wer es frueher braucht, ruft
 *    `sofort` von Hand.
 */
export function starteSichtbarenTakt(
  fn: () => void,
  ms: number,
  opts: { auchVersteckt?: number; nichtPausieren?: boolean } = {},
): () => void {
  // Ruckel-Test (Ticket 2912): „Live-Aktualisierungen pausieren“ — der letzte Schlag wird beim Ausschalten nachgeholt.
  const schlag = opts.nichtPausieren ? fn : gesteuert("live", fn, true);
  const timer = window.setInterval(() => {
    if (document.visibilityState === "visible") {
      schlag();
      return;
    }
    /*
     * Manche Schlaege duerfen im Hintergrund nicht ganz verstummen — der
     * App-Zustand etwa, sonst haelt der Server diesen Client fuer weg.
     * Sie schlagen dann seltener statt gar nicht.
     */
    const langsam = opts.auchVersteckt;
    if (!langsam) return;
    const jetzt = Date.now();
    const zuletzt = versteckteSchlaege.get(fn) ?? 0;
    if (jetzt - zuletzt < langsam) return;
    versteckteSchlaege.set(fn, jetzt);
    schlag();
  }, ms);
  return () => {
    window.clearInterval(timer);
    versteckteSchlaege.delete(fn);
  };
}

/** Wann eine gedrosselte Funktion zuletzt im Hintergrund geschlagen hat. */
const versteckteSchlaege = new WeakMap<() => void, number>();
